import {Component, OnInit, ViewChild} from '@angular/core';
import {CorePopupComponent} from '../../ui-components/core-popup/core-popup.component';
import {IcdCodeService} from '../../shared/components/icd-code/services/icd-code.service';
import {AppMessageService} from '../../shared/services/app-message/app-message.service';
import {AppAlertService} from '../../shared/services/app-alert/app-alert.service';
import {TranslateService} from '@ngx-translate/core';
import {IICDGroup, IUpdateICDGroup} from './interfaces/icd-groups.interface';
import {IcdCodesPopupComponent} from '../../shared/components/icd-code/icd-codes-popup/icd-codes-popup.component';

@Component({
  selector: 'app-icd-group',
  templateUrl: './icd-group.component.html',
  styleUrl: './icd-group.component.scss',
})
export class ICDGroupComponent implements OnInit {
  @ViewChild('popup') popup: CorePopupComponent;
  @ViewChild('icdCodePopup') icdCodePopup: IcdCodesPopupComponent;
 
  mode: 'edit' | 'create' = 'create';
  isSubmit: boolean = false;
  form: {
    id?: number;
    name: string;
    description: string;
    icdCodes: any[];
  } = {
    name: '',
    description: '',
    icdCodes: [],
  };

  groupFilter: string = '';
  codeFilter: string = '';

  groupList: IICDGroup[] = [];
  filteredGroupList: IICDGroup[] = [];
  icdCodes: { id: number; name: string, code: string }[] = [];
  selectedGroup: IICDGroup;
  systemLanguage: string;
  loading: boolean = false;

  constructor(
    private readonly icdService: IcdCodeService,
    private readonly messageService: AppMessageService,
    private readonly appAlertService: AppAlertService,
    private readonly translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.systemLanguage =
      this.translate?.currentLang || this.translate?.getDefaultLang() || 'tr';

    this.loading = true;
    this.icdService.getICDGroupList().subscribe({
      next: (res) => {
        this.groupList = res ?? [];
        this.filteredGroupList = [...this.groupList];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  onGroupFilter() {
    this.filteredGroupList = this.groupList.filter((item) => {
      return item?.name
        ?.toLocaleLowerCase(this.systemLanguage)
        ?.includes(this.groupFilter?.toLocaleLowerCase(this.systemLanguage));
    });
  }

  onCodeFilter() {
    this.icdCodes = this.selectedGroup.icdCodes.filter((item) => {
      return (item?.name
          ?.toLocaleLowerCase(this.systemLanguage)
          ?.includes(this.codeFilter?.toLocaleLowerCase(this.systemLanguage))) ||
        (item?.code
          ?.toLocaleLowerCase(this.systemLanguage)
          ?.includes(this.codeFilter?.toLocaleLowerCase(this.systemLanguage)));
    });
  }

  selectItem(item: any) {
    this.selectedGroup = this.selectedGroup?.id === item.id ? null : item;
    this.icdCodes = this.selectedGroup?.icdCodes ?? [];
  }

  addGroup(item?: any) {
    if (item) {
      this.mode = 'edit';
      this.form = {...item};
    }
    this.popup.openDialog();
  }

  deleteICDCodeAlert(code: { name: string; id: number }) {
    this.appAlertService.sweetConfirm({
      header: `${this.selectedGroup.name} ${this.translate.instant(
        'icd-codes.often-used.delete-icd-code-header'
      )}`,
      message: `"${code.name}" ${this.translate.instant(
        'icd-codes.often-used.delete-icd-code-text'
      )}`,
      showCloseButton: true, 
      accept: () => this.deleteICDCode(code),
    });
  }

  deleteICDCode(code: { name: string; id: number }) {
    this.selectedGroup.icdCodes = this.selectedGroup.icdCodes.filter(
      (icdCode) => icdCode?.id !== code?.id
    );
    this.form = {
      name: this.selectedGroup.name,
      description: this.selectedGroup.description,
      id: this.selectedGroup.id,
      icdCodes: this.selectedGroup.icdCodes,
    };
    this.updateGroup();
  }

  deleteGroupeAlert(group: IICDGroup) {
    this.appAlertService.sweetConfirm({
      header: `${group.name} ${this.translate.instant(
        'icd-codes.often-used.delete-group-header'
      )}`,
      message: this.translate.instant('icd-codes.often-used.delete-group-text'),
      showCloseButton: true, 
      accept: () => this.deleteGroup(group),
    });
  }

  deleteGroup(group: IICDGroup) {
    this.loading = true;
    this.icdService.deleteICDGroup(group.id).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          this.groupList = this.groupList.filter(
            (list) => list.id !== group.id
          );
          this.filteredGroupList = [...this.groupList];
          if (this.selectedGroup.id === group.id) {
            this.icdCodes = [];
            this.selectedGroup = null!;
          }
          this.translate.instant('common.success');
          this.translate.instant('common.deleteSuccessToastMessage');
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  onGroupPopupClosed() {
    this.isSubmit = false;
    this.mode = 'create';
    this.form = {
      name: '',
      description: '',
      icdCodes: [],
    };
  }

  openIcdCodePopup() {
    this.icdCodePopup.initilize();
  }

  icdCodeSave(icdCodes: { name: string; id: number, code: string }[]) {
    if (icdCodes.length > 0) {

      icdCodes = icdCodes.filter(code => !this.selectedGroup.icdCodes.some(c => c.id === code.id))
      this.selectedGroup.icdCodes = [
        ...this.selectedGroup.icdCodes,
        ...icdCodes,
      ];
      this.form = {
        name: this.selectedGroup.name,
        description: this.selectedGroup.description,
        id: this.selectedGroup.id,
        icdCodes: this.selectedGroup.icdCodes,
      };
      this.updateGroup();
    }
  }

  saveGroup() {
    this.isSubmit = true;
    if (this.isSubmit && this.form.name === '') {
      return;
    }

    this.loading = true;
    this.icdService
      .createICDGroup({
        name: this.form.name,
        description: this.form.description,
      })
      .subscribe({
        next: (res) => {
          this.groupList = [{...res, icdCodes: []}, ...this.groupList];
          this.filteredGroupList = [...this.groupList];
          this.messageService.successToast(
            this.translate.instant('common.success'),
            this.translate.instant('common.saveSuccessToastMessage')
          );
          this.popup.closeDialog();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  updateGroup() {
    this.isSubmit = true;
    if (this.isSubmit && this.form.name === '') {
      return;
    }

    const obj: IUpdateICDGroup = {
      id: this.form.id!,
      name: this.form.name,
      description: this.form.description,
      icdCodes: this.form.icdCodes,
    };
    this.icdCodes = this.form.icdCodes;
    this.loading = true;
    this.icdService.updateICDGroup(obj).subscribe({
      next: (res) => {
        const index = this.groupList.findIndex((gL) => gL.id === this.form.id);

        if (index > -1) {
          this.groupList[index] = {...res};
          this.filteredGroupList = [...this.groupList];
        }
        this.messageService.successToast(
          this.translate.instant('common.success'),
          this.translate.instant('common.updateSuccessToastMessage')
        );
        this.popup.closeDialog();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }
}
